import compact from "lodash/compact";

import {
  AudienceDto,
  AudienceFilter,
  AudienceFiltersDto,
  AudienceFilterType,
  ChannelType,
  CountryEnum,
  DeviceType,
  ExpressionNodeQueryType,
  ExpressionPayloadType,
  VisitorType,
} from "@intelligems/ig-types";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";

import { cookieTargeting } from "../../../../utils/linkSheet";

export const getInitialVisitorState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .find((query) => query?.type === "visitor")?.value ?? "any"
  );
};

export const getInitialChannelsState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  const valArray = state?.filters[queryParamIndex]?.expression
    ?.map((expression) => expression.query)
    .filter((query) => query?.type === "trafficSource")
    .map((query) => query?.value)
    .filter((val) => val);
  return valArray?.length ? valArray : ["any"];
};

export const getInitialCountriesState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    (state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .filter((query) => query?.type === "country")
      .map((query) => query?.value)
      .filter((val) => val) as string[]) ?? []
  );
};

export const getInitialDeviceState = (
  state: AudienceDto,
  queryParamIndex: number
) => {
  return (
    state?.filters[queryParamIndex]?.expression
      ?.map((expression) => expression.query)
      .find((query) => query?.type === "device")?.value ?? "any"
  );
};
export const getExpressionsState = (
  deviceState: DeviceType | null,
  visitorState: VisitorType | null,
  countryState: string[],
  channelState: ChannelType[]
) => {
  const expressions: ExpressionPayloadType[] = [];
  if (deviceState !== "any") {
    expressions.push({
      type: "device",
      queries: [
        {
          key: "device",
          value: deviceState!,
          filter: "equals",
          type: "device",
        },
      ],
    });
  }
  if (visitorState) {
    expressions.push({
      type: "visitor",
      queries: [
        {
          key: "visitor",
          value: visitorState,
          filter: "equals",
          type: "visitor",
        },
      ],
    });
  }
  if (channelState.length) {
    expressions.push({
      type: "trafficSource",
      queries: channelState.map((channel) => ({
        key: "trafficSource",
        value: channel,
        filter: "equals",
        type: "trafficSource",
      })),
    });
  }
  if (countryState.length) {
    expressions.push({
      type: "country",
      queries: countryState.map((country) => ({
        key: "country",
        value: country,
        filter: "equals",
        type: "country",
      })),
    });
  }
  return expressions;
};

export const formatCustomQueriesChip = (queryParam: AudienceFiltersDto) => {
  const queriesOnly = compact(
    queryParam?.expression?.map((expression) => expression.query) ?? []
  );
  const expressionsLength = queriesOnly.length;
  return `Custom (${capitalizeFirstLetter(queriesOnly[0].value!)} ${
    expressionsLength - 1 > 0 ? "+" : ""
  } ${expressionsLength - 1 > 0 ? expressionsLength - 1 : ""} ${
    expressionsLength - 1 > 0 ? "more" : ""
  })`;
};
export const formatQueries = (queries: ExpressionNodeQueryType[]) => {
  let countryLength = 0;
  let shouldShorten = false;
  if (queries[0].type === "country") {
    countryLength = queries.length;
  }
  if (countryLength > 3) {
    shouldShorten = true;
  }
  return `${queries
    .slice(0, shouldShorten ? 4 : queries.length)
    .map((query) =>
      CountryEnum[query.value!]
        ? `${CountryEnum[query.value!]} (${query.value})`
        : capitalizeFirstLetter(query.value!)
    )
    .join(", ")
    .replace(/, ([^,]*)$/, " or $1")} ${
    shouldShorten && queries.length !== 4 ? "+" : ""
  } ${shouldShorten && queries.length !== 4 ? queries.length - 4 : ""} ${
    shouldShorten && queries.length !== 4 ? "more" : ""
  }`;
};

export const conditionTypeOptions: {
  title: string;
  key: AudienceFilterType;
}[] = [
  { key: "utm", title: "URL Param" },
  // { key: "url", title: "URL Param" },
  { key: "device", title: "Device" },
  { key: "visitor", title: "New or Returning" },
  { key: "jsExpression", title: "Javascript" },
  { key: "trafficSource", title: "Traffic Source" },
  { key: "country", title: "Country" },
  { key: "referrer", title: "Referring Domain" },
  { key: "cookie", title: "Cookie" },
  { key: "landingPage", title: "Landing Page" },
  // { key: "klaviyo", title: "Klaviyo Audience ID" },
];

export const conditionFilterOptions: {
  key: AudienceFilter;
  title: string;
}[] = [
  { key: "equals", title: "equals" },
  { key: "contains", title: "contains" },
  { key: "startsWith", title: "starts with" },
  { key: "endsWith", title: "ends with" },
  { key: "doesNotEqual", title: "does not equal" },
  { key: "doesNotContain", title: "does not contain" },
  { key: "doesNotStartWith", title: "does not start with" },
  { key: "doesNotEndWith", title: "does not end with" },
  { key: "isNull", title: "is null" },
  { key: "isNotNull", title: "is not null" },
  { key: "regex", title: "regex" },
];

const alternateFilterLabels = {
  landingPage: {
    equals: "matches exactly",
    regex: "matches regex",
  },
};

export const getAlternateFilterLabel = (
  key: AudienceFilter,
  type: AudienceFilterType
) => {
  return alternateFilterLabels[type]?.[key] ?? null;
};

export interface HintType {
  text: string;
  link?: string;
  tracker?: string;
}
const hints = {
  cookie: {
    text: "Intelligems can only target first party insecure cookies, for example those of Klaviyo and Google Analytics. Click to learn more.",
    link: cookieTargeting,
    tracker: "Cookie Targeting",
  },
  landingPage: {
    text: "Intelligems will assume https:// protocol. All values are case insensitive. Query params, trailing slashes, www., will be ignored. Special characters will be decoded.",
    // link: "landing page link",
    // tracker: "landing page targeting",
  },
};

export const getHint = (type: AudienceFilterType): HintType => {
  return hints[type];
};
